import { CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { catchError, finalize, firstValueFrom, from, of } from "rxjs";
import { ActionButtonComponent } from "../ui-components/buttons/action-button/action-button.component";
import { FormInputComponent } from "../ui-components/forms/form-input/form-input.component";
import { PaymentDialogBase } from "../payment-dialog-base/payment-dialog-base";
import { PaymentDialogModel } from "src/app/models/payment-dialog.model";
import { TenderType } from "src/app/services/model/tender-type.enum";
import { VoucherApiService } from "src/app/services/api/voucher.service";
import { IVoucherStatusItem } from "src/app/services/model/pos.api.model";

interface VoucherPaymentDialogModel extends PaymentDialogModel {
  voucherCode?: string | null;
  voucherBnr?: number;
  voucherSource?: string;
}

@Component({
  selector: "app-voucher-payment-dialog",
  templateUrl: "./voucher-payment-dialog.component.html",
  styleUrls: ["./voucher-payment-dialog.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormInputComponent,
    ActionButtonComponent,
    ReactiveFormsModule,
  ],
})
export class VoucherPaymentDialogComponent extends PaymentDialogBase {
  private readonly _voucherService = inject(VoucherApiService);
  private static readonly VOUCHER_CASH_SUBTYPE = "VOUCHERCASH";
  private static readonly VOUCHER_BENEFIT_SUBTYPE = "VOUCHERBENEFIT";

  public isCheckingVoucherStatus = false;
  public enteredVoucherCode = "";
  private statusRequestVersion = 0;
  public voucherStatusItem?: IVoucherStatusItem;
  public voucherStatusMessage: string | null = null;

  get tenderType(): TenderType {
    return TenderType.Voucher;
  }

  protected getPaymentTranslationKey(): string {
    return "webPos.Trip.ApplyVoucher";
  }

  protected get voucherData(): VoucherPaymentDialogModel {
    return this.data as VoucherPaymentDialogModel;
  }

  get voucherCode(): string | null {
    return this.voucherData.voucherCode ?? this.enteredVoucherCode;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.confirmForm.addControl("voucherGroupType", new FormControl<string | null>(null));
    this.confirmForm.addControl("voucherTenderSubType", new FormControl<string | null>(null));
    this.loadVoucherStatus();
  }

  public get voucherValid(): boolean {
    return !!this.voucherStatusItem;
  }

  public override async onConfirm() {
    if (!this.voucherValid || this.isCheckingVoucherStatus) {
      return;
    }

    if (this.confirmForm.invalid || !this.isFormValid()) {
      this.confirmForm.markAllAsTouched();
      this.confirmForm.updateValueAndValidity();
      return;
    }

    const amountPaid = this.confirmForm.get("amountToPay")?.value;
    const tenderSubType = (this.confirmForm.get("voucherTenderSubType")?.value || "").toString().toUpperCase();
    const paymentTypes = await this.getAvailablePaymentTypes();

    const paymentType =
      paymentTypes.find(
        (pt) =>
          pt.TenderType === TenderType.Voucher
          && pt.TenderSubType?.toUpperCase() === tenderSubType
      );
    if (!paymentType) {
      this.voucherStatusMessage = this.translate.instant("webPos.Voucher.VoucherNotValid");
      return;
    }

    this.dialogResult.Amount = Number(amountPaid);
    this.dialogResult.PaymentType = TenderType.Voucher;
    this.dialogResult.PaymentName = paymentType?.Name || "";
    this.dialogResult.Currency = this.loginService.user.CurrencyCode;

    this.onBeforeClose();
    this.dialogRef.close(this.dialogResult);
  }

  protected override onBeforeClose() {
    super.onBeforeClose();
    const tenderSubType = this.confirmForm.get("voucherTenderSubType")?.value;
    const voucherGroupType = this.confirmForm.get("voucherGroupType")?.value;

    this.dialogResult.PaymentAttributes ??= [];

    if (tenderSubType) {
      this.dialogResult.PaymentAttributes.push({
        Key: "TenderSubType",
        Value: tenderSubType,
      });
    }

    if (voucherGroupType) {
      this.dialogResult.PaymentAttributes.push({
        Key: "VoucherGroupType",
        Value: voucherGroupType,
      });
    }

    if (this.voucherCode) {
      this.dialogResult.PaymentAttributes.push({
        Key: "VoucherCode",
        Value: this.voucherCode,
      });
    }
  }

  public onVoucherCodeChange(value: string | number | null): void {
    this.enteredVoucherCode = String(value ?? "").trim();
    this.statusRequestVersion++;
    this.isCheckingVoucherStatus = false;
    this.voucherStatusItem = undefined;
    this.voucherStatusMessage = null;
  }

  public get hasPresetVoucherCode(): boolean {
    return !!this.voucherData.voucherCode;
  }

  public async loadVoucherStatus(): Promise<void> {
    const requestVersion = ++this.statusRequestVersion;
    const voucherBnr = this.voucherData.voucherBnr;
    const voucherSource = this.voucherData.voucherSource ?? "PosNG";
    const code = this.voucherCode?.trim();

    if (!code || !Number.isFinite(voucherBnr)) {
      this.voucherStatusMessage = this.translate.instant("webPos.Voucher.VoucherNotValid");
      this.voucherStatusItem = undefined;
      return;
    }

    this.isCheckingVoucherStatus = true;
    this.voucherStatusMessage = null;

    const statusResult = await firstValueFrom(
      from(this._voucherService.getVoucherStatus(voucherBnr, voucherSource)).pipe(
        catchError(() => {
          return of(null);
        }),
        finalize(() => {
          if (requestVersion === this.statusRequestVersion) this.isCheckingVoucherStatus = false;
        }),
      ),
    );

    if (requestVersion !== this.statusRequestVersion) return;

    if (statusResult?.success) {
      this.voucherStatusItem = statusResult.vouchers?.find(
        (item) => item.sapId === code || item.groupTypeName?.trim() === code,
      );
      if (!this.voucherStatusItem) {
        this.voucherStatusMessage = this.translate.instant("webPos.Voucher.VoucherNotValid");
      } else {
        const groupType = this.voucherStatusItem.groupType?.trim().toLowerCase();
        const tenderSubType = this.mapGroupTypeToTenderSubType(groupType);

        const paymentTypes = await this.getAvailablePaymentTypes();
        if (requestVersion !== this.statusRequestVersion) return;
        if (!tenderSubType || !paymentTypes.some(payment =>
          payment.TenderType === TenderType.Voucher && payment.TenderSubType?.toUpperCase() === tenderSubType)
          || this.voucherStatusItem.voucherCurrency !== this.loginService.user.CurrencyCode) {
          this.voucherStatusItem = undefined;
          this.voucherStatusMessage = this.translate.instant("webPos.Voucher.VoucherNotValid");
          return;
        }

        this.confirmForm.get("voucherGroupType")?.patchValue(groupType);
        this.confirmForm.get("voucherTenderSubType")?.patchValue(tenderSubType);

        const voucherAmount = Number.parseFloat(this.voucherStatusItem.voucherPrice) || 0;
        const amountDue = this.amountToPay ?? 0;
        const amountToUse = amountDue > 0 ? Math.min(voucherAmount, amountDue) : voucherAmount;
        this.confirmForm.get("amountToPay")?.patchValue(amountToUse);
      }
    } else {
      this.voucherStatusMessage = this.translate.instant("webPos.Voucher.VoucherNotValid");
      this.voucherStatusItem = undefined;
    }
  }

  private mapGroupTypeToTenderSubType(groupType?: string | null): string | null {
    if (!groupType) {
      return null;
    }

    if (groupType === "b") {
      return VoucherPaymentDialogComponent.VOUCHER_BENEFIT_SUBTYPE;
    } else {
      return VoucherPaymentDialogComponent.VOUCHER_CASH_SUBTYPE;
    }

    return null;
  }
}
